import { useState } from "react";
import {
  Space,
  Table,
  Tag,
  Popconfirm,
  Switch,
  Pagination,
  Button,
  Modal,
  Input,
  Form,
  message,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { IoTrashOutline, IoLockOpenOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { BiEdit } from "react-icons/bi";
import { RiLockPasswordLine } from "react-icons/ri";
import { SiMicrosoftexcel } from "react-icons/si";
import { PUT } from "../../../utils/apirequest";
import { API } from "../../../config/API";

function Datatable(props: any) {
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const navigate = useNavigate();
  const [passwordModal, setPasswordModal] = useState<any>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [pwForm] = Form.useForm();

  const passwordRules = [
    { label: "Minimum 8 characters", test: (v: string) => v.length >= 8 },
    { label: "At least one lowercase letter", test: (v: string) => /[a-z]/.test(v) },
    { label: "At least one uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
    { label: "At least one number", test: (v: string) => /\d/.test(v) },
    { label: "At least one special character", test: (v: string) => /[!@#$%^&*()_+[\]{};':"\\|,.<>?]/.test(v) },
  ];

  const [pwValue, setPwValue] = useState("");

  const allPwRulesPassed = pwValue ? passwordRules.every((r) => r.test(pwValue)) : false;

  const handlePasswordUpdate = async () => {
    try {
      const values = await pwForm.validateFields();
      setPasswordLoading(true);
      let url = API.PUT_UPDATE_USER + `${passwordModal.id}`;
      await PUT(url, { password: values.password });
      message.success("Password updated successfully");
      setPasswordModal(null);
      pwForm.resetFields();
      setPwValue("");
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error("Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChange = (pagination: any, filters: any, sorter: any) => {
    setSortedInfo(sorter);
  };

  const handleSwitchChange = (checked: any, id: any) => {
    props.onUpdate(id, checked);
  };

  interface DataType {
    key: string;
    id: number;
    username: string;
    news: string;
    newshead: string;
    address: string;
    imageurl: string[];
  }

  const columns: ColumnsType<DataType> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 50,
      render: (text, record, index) => {
        return <div className="">{index + 1}</div>;
      },
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 500,
      sorter: (a: any, b: any) => a.username.localeCompare(b.username),
      sortOrder: sortedInfo.columnKey === "username" && sortedInfo.order,
      render: (text, record: any) => {
        return (
          <div onClick={() => navigate(`/Auth/users/form/${record.id}`)}>
            {text}
          </div>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "Role",
      key: "role",
      width: 500,
      render: (text, record: any) => {
        return (
          <Tag onClick={() => navigate(`/Auth/users/form/${record.id}`)}>
            {record?.role?.name}
          </Tag>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 500,
      render: (text, record: any) => {
        return (
          <div onClick={() => navigate(`/Auth/users/form/${record.id}`)}>
            {text}
          </div>
        );
      },
    },
    {
      title: "Posts",
      dataIndex: "all_news",
      key: "all_news",
      width: 500,
      render: (text, record: any) => {
        return (
          <div style={{ fontSize: "10px" }}>{record?.all_news?.count}</div>
        );
      },
    },
    {
      title: "Export Log",
      key: "Export",
      width: 70,
      render: (text, record: any) => {
        return (
          <>
            <Button
              onClick={() => {
                props?.DoExport();
                props?.SelectedLog(record);
              }}
              loading={props?.loading3[record.id]}
              icon={<SiMicrosoftexcel color="green" />}
            >
              Export
            </Button>
          </>
        );
      },
    },
    {
      title: "Status",
      key: "publishedAt",
      width: 200,
      render: (text, record: any) => {
        return (
          <Space size="middle">
            <Tag
              color={record?.blocked ? "red" : "green"}
              style={{ fontSize: "10px" }}
            >
              {record?.blocked ? "Inactive" : "Active"}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: "Blocked",
      key: "blocked",
      width: 70,
      render: (text, record: any) => {
        return (
          <Switch
            checked={record?.blocked}
            loading={props.blockingId === record?.id}
            onChange={(val) => handleSwitchChange(val, record?.id)}
          />
        );
      },
    },

    {
      title: "Action",
      key: "action",
      render: (_, record: any) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-evenly",
              alignContent: "center",
            }}
          >
              {record?.isLocked ? (
              <Popconfirm
                title="Unlock User"
                description={`Unlock "${record.username}"? This resets failed login attempts.`}
                okText="Yes"
                cancelText="No"
                onConfirm={() => props.onUnlock(record.id)}
              >
                <Space size="middle" style={{ marginRight: 10 }}>
                  <IoLockOpenOutline
                    size={20}
                    color="#1677ff"
                    cursor={"pointer"}
                    title="Unlock user"
                  />
                </Space>
              </Popconfirm>
            ) : null}
            <Space size="middle" style={{ marginRight: 10 }}>
              <BiEdit
                size={20}
                color="#f5a442"
                cursor={"pointer"}
                onClick={() => navigate(`/Auth/users/form/${record.id}`)}
              />
            </Space>
            <Space size="middle" style={{ marginRight: 10 }}>
              <Tooltip title="Change Password">
                <RiLockPasswordLine
                  size={20}
                  color="#0055a6"
                  cursor={"pointer"}
                  onClick={() => {
                    setPasswordModal(record);
                    pwForm.resetFields();
                    setPwValue("");
                  }}
                />
              </Tooltip>
            </Space>
            <Popconfirm
              title="Delete User"
              description={`Are you sure you want to delete "${record.username}"?`}
              okText="Yes"
              cancelText="No"
              onConfirm={() => props.onDelete(record.id)}
            >
              <Space size="middle">
                <IoTrashOutline size={20} color="red" cursor={"pointer"} />
              </Space>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ marginTop: 10 }}>
      <Table
        columns={columns}
        dataSource={props.data}
        pagination={false}
        onChange={handleChange}
        size="small"
        rowKey="id"
      />
      <br />
      <div style={{ float: "right" }}>
        <Pagination
          responsive
          defaultCurrent={props?.page}
          total={props.meta && props.meta}
          pageSize={props?.pagesize}
          onChange={(p, t) => {
            props.onPageChange(p, t);
          }}
        />
      </div>
      <Modal
        title={`Change Password — ${passwordModal?.username}`}
        open={!!passwordModal}
        onCancel={() => { setPasswordModal(null); pwForm.resetFields(); setPwValue(""); }}
        onOk={handlePasswordUpdate}
        okText="Update Password"
        confirmLoading={passwordLoading}
        okButtonProps={{ disabled: !allPwRulesPassed }}
      >
        <Form form={pwForm} layout="vertical">
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              {
                validator: (_: any, value: any) => {
                  if (!value) return Promise.reject("Please enter a password");
                  if (passwordRules.every((r) => r.test(value))) return Promise.resolve();
                  return Promise.reject("");
                },
              },
            ]}
            style={pwValue && !allPwRulesPassed ? { marginBottom: 4 } : {}}
            help={pwValue && !allPwRulesPassed ? "" : undefined}
          >
            <Input.Password
              size="large"
              placeholder="Enter new password"
              onChange={(e) => setPwValue(e.target.value)}
            />
          </Form.Item>
          {pwValue && !allPwRulesPassed && (
            <div style={{ marginBottom: 12 }}>
              {passwordRules.map((rule, i) => {
                const passed = rule.test(pwValue);
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "2px 0",
                      fontSize: 13,
                      color: passed ? "#52c41a" : "#999",
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{passed ? "✓" : "✕"}</span>
                    {rule.label}
                  </div>
                );
              })}
            </div>
          )}
          <Form.Item
            name="confirm"
            label="Confirm Password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value)
                    return Promise.resolve();
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password size="large" placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Datatable;
